import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FiArrowLeft, FiClock, FiTag, FiUser, FiMessageCircle } from 'react-icons/fi';
import { MdDescription } from 'react-icons/md';
import * as documentService from '../../services/documents';
import * as commentService from '../../services/comments';
import { useSocket } from '../../context/SocketContext';
import '../../styles/main.scss';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { socket, joinDocument, leaveDocument } = useSocket();

  const [newComment, setNewComment] = useState('');

  const {
    data: documentResponse,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
  } = useQuery(
    ['document', id],
    () => documentService.getDocument(id),
    {
      enabled: !!id,
    }
  );

  const {
    data: commentsResponse,
    isLoading: isCommentsLoading,
    isError: isCommentsError,
  } = useQuery(
    ['comments', id],
    () => commentService.getComments(id, { includeReplies: 'true' }),
    {
      enabled: !!id,
    }
  );

  const createCommentMutation = useMutation(
    ({ documentId, content }) =>
      commentService.createComment(documentId, content),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['comments', id]);
        setNewComment('');
        toast.success('Comment added');
      },
      onError: () => {
        toast.error('Failed to add comment');
      },
    }
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    joinDocument(id);

    return () => {
      leaveDocument(id);
    };
  }, [id, joinDocument, leaveDocument]);

  useEffect(() => {
    if (!socket || !id) {
      return;
    }

    const handleCommentEvent = () => {
      queryClient.invalidateQueries(['comments', id]);
    };

    socket.on('comment-added', handleCommentEvent);
    socket.on('comment-updated', handleCommentEvent);
    socket.on('comment-deleted', handleCommentEvent);

    return () => {
      socket.off('comment-added', handleCommentEvent);
      socket.off('comment-updated', handleCommentEvent);
      socket.off('comment-deleted', handleCommentEvent);
    };
  }, [socket, id, queryClient]);

  const handleAddComment = (event) => {
    event.preventDefault();

    if (!newComment.trim()) {
      return;
    }

    if (!id) {
      return;
    }

    createCommentMutation.mutate({
      documentId: id,
      content: newComment.trim(),
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }
    return new Date(value).toLocaleString();
  };

  const formatDateShort = (value) => {
    if (!value) {
      return '-';
    }
    return new Date(value).toLocaleDateString();
  };

  if (isDocumentLoading) {
    return (
      <div className="dashboard-page document-detail-page">
        <div className="page-header">
          <div>
            <div className="skeleton skeleton-text" style={{ width: '240px', height: '32px' }} />
            <div className="skeleton skeleton-text" style={{ width: '320px', height: '20px', marginTop: '0.5rem' }} />
          </div>
        </div>
        <div className="page-content">
          <div className="skeleton skeleton-card" style={{ height: '260px', marginBottom: '1.5rem' }} />
          <div className="skeleton skeleton-card" style={{ height: '260px' }} />
        </div>
      </div>
    );
  }

  if (isDocumentError || !documentResponse?.data?.document) {
    return (
      <div className="dashboard-page document-detail-page">
        <div className="page-header">
          <div>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
            <h1>Document not found</h1>
            <p>We could not load this document. It may have been removed or you may not have permission to view it.</p>
          </div>
        </div>
      </div>
    );
  }

  const documentData = documentResponse.data;
  const document = documentData.document;
  const similarDocuments = documentData.similarDocuments || [];

  const commentsData = commentsResponse?.data || [];
  const commentsMeta = commentsResponse?.meta;

  return (
    <div className="dashboard-page document-detail-page">
      <div className="page-header">
        <div>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft />
            <span>Back to documents</span>
          </button>
          <h1>{document.name}</h1>
          <p>{document.description || 'No description provided for this document.'}</p>
        </div>
        <div className="document-summary-meta">
          <div className="document-status">
            <span className={`badge status-${document.status}`}>{document.status}</span>
            <span className={`badge priority-${document.priority}`}>{document.priority} priority</span>
          </div>
          <div className="document-summary-details">
            {document.documentNumber && (
              <span className="meta-item">
                <MdDescription />
                <span>{document.documentNumber}</span>
              </span>
            )}
            <span className="meta-item">
              <FiClock />
              <span>Created {formatDateShort(document.createdAt)}</span>
            </span>
            {document.categoryId && (
              <span className="meta-item">
                <FiTag />
                <span>{document.categoryId.name}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="document-detail-layout">
          <div className="document-main">
            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="section-header">
                <h2>Details</h2>
              </div>
              <div className="document-details-grid">
                <div className="detail-item">
                  <span className="detail-label">Type</span>
                  <span className="detail-value text-capitalize">{document.type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value text-capitalize">{document.status}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Priority</span>
                  <span className="detail-value text-capitalize">{document.priority}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Due date</span>
                  <span className="detail-value">{formatDateShort(document.dueDate)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span className="detail-value">
                    {document.categoryId ? document.categoryId.name : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Folder</span>
                  <span className="detail-value">
                    {document.folderId ? document.folderId.name : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Owner</span>
                  <span className="detail-value">
                    {document.owner ? document.owner.name : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created by</span>
                  <span className="detail-value">
                    {document.createdBy ? document.createdBy.name : '-'}
                  </span>
                </div>
              </div>
              {Array.isArray(document.tags) && document.tags.length > 0 && (
                <div className="document-tags">
                  <span className="detail-label">Tags</span>
                  <div className="tags-list">
                    {document.tags.map((tag) => (
                      <span key={tag} className="badge badge-light">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="section-header">
                <h2>Comments</h2>
              </div>

              <form onSubmit={handleAddComment} className="comment-form">
                <div className="comment-input-wrapper">
                  <textarea
                    className="form-control"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(event) => setNewComment(event.target.value)}
                    rows={3}
                    disabled={createCommentMutation.isLoading}
                  />
                </div>
                <div className="comment-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createCommentMutation.isLoading || !newComment.trim()}
                  >
                    <FiMessageCircle />
                    <span>Post comment</span>
                  </button>
                </div>
              </form>

              <div className="comments-list">
                {isCommentsLoading ? (
                  <div className="loading-placeholder">
                    {[...Array(3)].map((_, index) => (
                      <div key={index} className="skeleton skeleton-text" style={{ height: '48px', marginBottom: '0.75rem' }} />
                    ))}
                  </div>
                ) : isCommentsError ? (
                  <div className="error-state">
                    <p>Failed to load comments.</p>
                  </div>
                ) : commentsData.length === 0 ? (
                  <div className="empty-state">
                    <p>No comments yet. Start the discussion.</p>
                  </div>
                ) : (
                  commentsData.map((comment) => (
                    <div key={comment._id} className="comment-item">
                      <div className="comment-header">
                        <div className="comment-author">
                          <div className="comment-avatar">
                            {comment.userId?.avatar ? (
                              <img src={comment.userId.avatar} alt={comment.userId.name} />
                            ) : (
                              <span>
                                {comment.userId?.name ? comment.userId.name.charAt(0) : '?'}
                              </span>
                            )}
                          </div>
                          <div className="comment-author-info">
                            <div className="comment-author-name">
                              {comment.userId?.name || 'Unknown'}
                            </div>
                            <div className="comment-meta">
                              <span>{formatDate(comment.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="comment-content">
                        <p>{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
                {commentsMeta && commentsMeta.total > commentsData.length && (
                  <div className="comments-footer">
                    <p className="text-sm text-muted">
                      Showing {commentsData.length} of {commentsMeta.total} comments.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          <div className="document-sidebar">
            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="section-header">
                <h2>Assignments</h2>
              </div>
              {Array.isArray(document.assignedTo) && document.assignedTo.length > 0 ? (
                <ul className="assigned-users-list">
                  {document.assignedTo.map((assignment) => (
                    <li key={assignment._id || assignment.user?._id}>
                      <div className="assigned-user">
                        <div className="assigned-avatar">
                          {assignment.user?.avatar ? (
                            <img src={assignment.user.avatar} alt={assignment.user.name} />
                          ) : (
                            <span>
                              {assignment.user?.name ? assignment.user.name.charAt(0) : '?'}
                            </span>
                          )}
                        </div>
                        <div className="assigned-info">
                          <div className="assigned-name">
                            {assignment.user?.name || 'Unknown'}
                          </div>
                          <div className="assigned-meta">
                            <span>{assignment.status || 'pending'}</span>
                            {assignment.assignedAt && (
                              <span> · {formatDateShort(assignment.assignedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">No users assigned to this document.</p>
              )}
            </motion.div>

            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="section-header">
                <h2>Activity</h2>
              </div>
              <div className="activity-list">
                <div className="activity-item">
                  <FiUser />
                  <span>
                    Created by {document.createdBy ? document.createdBy.name : 'Unknown'} on{' '}
                    {formatDateShort(document.createdAt)}
                  </span>
                </div>
                {document.lastModifiedBy && (
                  <div className="activity-item">
                    <FiUser />
                    <span>
                      Last updated by {document.lastModifiedBy.name} on{' '}
                      {formatDateShort(document.updatedAt)}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>

            {similarDocuments.length > 0 && (
              <motion.div
                className="section-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="section-header">
                  <h2>Similar documents</h2>
                </div>
                <ul className="similar-documents-list">
                  {similarDocuments.map((item) => (
                    <li key={item._id}>
                      <Link to={`/documents/${item._id}`} className="similar-document-link">
                        <div className="similar-document-icon">
                          <MdDescription />
                        </div>
                        <div className="similar-document-info">
                          <div className="similar-document-name">{item.name}</div>
                          <div className="similar-document-meta">
                            <span className="text-sm">
                              {item.documentNumber || 'No number'}
                            </span>
                            <span className="text-sm">
                              · {item.status}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;
